import React from 'react';

import { ModalProvider } from '@/context/ModalProvider';

import useProfile from '@/hooks/useProfile';

import { Header, Slider } from '@/components/Template/Template1';
import { Title } from '@/components/Template/Template1/Title';
import { Description } from '@/components/Template/Template1/Description';
import { MoreInfo } from '@/components/Template/Template1/MoreInfo';
import { Rules } from '@/components/Template/Template1/Rules';
import { Button } from '@/components/Template/Template1/Button';
import {
  getLayoutClass,
  getTheme,
  getThemeClass,
} from '@/components/Template/Template1/utils';
import { Banner } from '@/components/Template/Template1/Banner';
import { Image } from '@/components/Template/Template1/Image';
import { Notes } from '@/components/Template/Template1/Notes';
import { Section } from '@/components/global/Section';
import { Options } from '@/components/Template/Template1/Options';
import { Tabs } from '@/components/global/Tabs';
import { CardOfferGroup } from '@/components/Template/Template1/CardOfferGroup';
import { Subtitle } from '@/components/Template/Template1/Subtitle';

import { TRecommendation } from '@/services/requests/Recommendation/types/template1';

import { getValue } from '@/helpers/GetValueHelper';

import useTabs from './useTabs';
import { getContainerByPlace, hasOffers } from './utils';

type TProps = {
  data: TRecommendation[];
};

const TemplateVitrine1 = ({ data }: TProps) => {
  const {
    tabItems,
    hasTabs,
    hasOptions,
    getTabActive,
    getTabName,
    onTabClick,
    onOptionClick,
  } = useTabs(data);
  const { getLayoutByRecommendationId, profile } = useProfile();
  const layout = getLayoutByRecommendationId(data[0].id);

  function renderTemplateWithArray(dataArr: TRecommendation[]) {
    return (
      <>
        {hasTabs ? <Tabs items={tabItems} onClick={onTabClick} /> : <></>}
        {dataArr.map(item => {
          const itemClassName = 'vitrine-template1__item';
          const itemIsActive = `${getTabActive(item) ? '--active' : ''}`;
          return (
            <div
              key={`tab-content-${item.idLiferay}`}
              className={`${itemClassName}${itemIsActive}`}
            >
              {renderTemplateWithObject(item)}
            </div>
          );
        })}
      </>
    );
  }

  function renderTemplateWithObject(dataObj: TRecommendation) {
    return (
      <>
        <Header data={dataObj} />
        <Title data={dataObj} />
        <Image data={dataObj} />
        <Description data={dataObj} />
        <Button data={dataObj} tabName={getTabName(dataObj)} layout={layout} />
        <Banner data={dataObj} />
        <Slider
          data={dataObj}
          tabName={getTabName(dataObj)}
          tabActive={!!getTabActive(dataObj)}
        />
        {hasOffers(dataObj) && <CardOfferGroup data={dataObj} />}
        {hasOffers(dataObj) && <Subtitle data={dataObj} />}
        <MoreInfo data={dataObj} />
        <Notes data={dataObj} />
        {dataObj.rules && (
          <Rules
            rules={dataObj.rules}
            layout={layout}
            title={dataObj.title}
            events_tag={dataObj.events_tag}
          />
        )}
        {hasOptions && (
          <Options
            text={getValue(dataObj, 'variant_header', '') as string}
            idLiferay={dataObj.idLiferay}
            items={tabItems}
            onClick={onOptionClick}
          />
        )}
      </>
    );
  }

  return (
    <ModalProvider theme={getTheme(data[0])}>
      <Section
        className={`vitrine-template1 ${getLayoutClass(layout)} ${getThemeClass(
          data[0],
        )}`}
        data={data}
      >
        <div
          className={`vitrine-template1__container ${getContainerByPlace(
            profile.place,
          )}`}
          data-testid="vitrine-select"
        >
          {renderTemplateWithArray(data)}
        </div>
      </Section>
    </ModalProvider>
  );
};

export default TemplateVitrine1;
